/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   DependenciesTopologyPage.tsx
 *     Feature         :   217-insights-dashboards (T037 / US7, FR-006)
 *     Description     :   Dependencies Topology — the Clarify-locked hybrid design: a
 *                         collapsible tree (capacity → workspace → lakehouse/warehouse)
 *                         with a shared-resource side lane (storage accounts bound by more
 *                         than one entity render once, with a "shared by N" badge).
 *                         Nested kit components only — no new graph-visualization
 *                         dependency (per Clarify; escalate before adding one).
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-05
 * =========================================================================================
 */

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { usePageData } from '../../hooks/usePageData';
import { useNavigate } from 'react-router-dom';
import { GitBranch, ChevronRight, ChevronDown, Database, Share2 } from 'lucide-react';
import { apiClient } from '../../services/apiClient';
import { PageLayout, Card, Badge, ErrorState, LoadingState, Extended, QuickGlance } from '../../components/ui';

interface DependenciesTopologyPayload {
  capacities: Array<{ id: string; name: string }>;
  workspaces: Array<{ id: string; name: string; capacityId: string | null }>;
  lakehouses: Array<{ id: string; name: string; workspaceId: string }>;
  warehouses: Array<{ id: string; name: string; workspaceId: string }>;
  storageBindings: Array<{ storageAccountId: string; storageAccountName: string; entityType: 'Lakehouse' | 'Warehouse'; entityId: string; entityName: string }>;
  capacityDependencies: Array<{ dependentCapacityId: string; dependentCapacityName: string; dependencyCapacityId: string; dependencyCapacityName: string; dependencyType: string; impactSeverity: string }>;
}

function WorkspaceNode({ ws, lakehouses, warehouses, onNavigate }: {
  ws: DependenciesTopologyPayload['workspaces'][number];
  lakehouses: DependenciesTopologyPayload['lakehouses'];
  warehouses: DependenciesTopologyPayload['warehouses'];
  onNavigate: (path: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const items = [
    ...lakehouses.filter((l) => l.workspaceId === ws.id).map((l) => ({ ...l, kind: 'Lakehouse' as const })),
    ...warehouses.filter((w) => w.workspaceId === ws.id).map((w) => ({ ...w, kind: 'Warehouse' as const })),
  ];
  return (
    <div className="ml-4 border-l border-[var(--color-border)] pl-3">
      <button className="flex w-full items-center gap-1.5 py-1 text-left text-sm hover:text-[var(--color-accent)]" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <span className="text-[var(--color-text-primary)]">{ws.name}</span>
        <span className="text-xs text-[var(--color-text-secondary)]">({items.length} items)</span>
      </button>
      {open && (
        <div className="ml-4 space-y-0.5 border-l border-[var(--color-border)] pl-3">
          {items.length === 0 && <p className="py-1 text-xs text-[var(--color-text-secondary)]">No lakehouses/warehouses</p>}
          {items.map((item) => (
            <button
              key={`${item.kind}-${item.id}`}
              className="flex w-full items-center gap-1.5 py-0.5 text-left text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-accent)]"
              onClick={() => onNavigate(item.kind === 'Lakehouse' ? `/lakehouses/${item.id}` : `/warehouses/${item.id}`)}
            >
              <Database className="h-3 w-3" />
              {item.name} <Badge variant="secondary" isOutlined size="sm">{item.kind}</Badge>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CapacityNode({ capacity, workspaces, lakehouses, warehouses, onNavigate }: {
  capacity: DependenciesTopologyPayload['capacities'][number];
  workspaces: DependenciesTopologyPayload['workspaces'];
  lakehouses: DependenciesTopologyPayload['lakehouses'];
  warehouses: DependenciesTopologyPayload['warehouses'];
  onNavigate: (path: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wsForCapacity = workspaces.filter((w) => w.capacityId === capacity.id);
  return (
    <Card padding="sm">
      <button className="flex w-full items-center gap-1.5 text-left text-sm font-medium hover:text-[var(--color-accent)]" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <span className="text-[var(--color-text-primary)]">{capacity.name}</span>
        <span className="text-xs text-[var(--color-text-secondary)]">({wsForCapacity.length} workspaces)</span>
      </button>
      {open && (
        <div className="mt-1">
          {wsForCapacity.length === 0 && <p className="py-1 pl-4 text-xs text-[var(--color-text-secondary)]">No workspaces</p>}
          {wsForCapacity.map((ws) => (
            <WorkspaceNode key={ws.id} ws={ws} lakehouses={lakehouses} warehouses={warehouses} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </Card>
  );
}

export const DependenciesTopologyPage: React.FC = () => {
  const navigate = useNavigate();
  const query = useQuery<DependenciesTopologyPayload>({
    queryKey: ['dependencies-topology'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: DependenciesTopologyPayload }>('/api/dependencies-topology');
      return res.data.data;
    },
    staleTime: 60_000,
  });
  const { data, isLoading, error, refresh, isRefreshing, lastFetchedAt } = usePageData(query);

  const sharedResources = useMemo(() => {
    if (!data) return [];
    const byAccount = new Map<string, { name: string; entities: DependenciesTopologyPayload['storageBindings'] }>();
    for (const b of data.storageBindings) {
      const existing = byAccount.get(b.storageAccountId);
      if (existing) existing.entities.push(b);
      else byAccount.set(b.storageAccountId, { name: b.storageAccountName, entities: [b] });
    }
    return Array.from(byAccount.entries())
      .map(([id, v]) => ({ storageAccountId: id, name: v.name, entities: v.entities, sharedByCount: v.entities.length }))
      .filter((r) => r.sharedByCount > 1)
      .sort((a, b) => b.sharedByCount - a.sharedByCount);
  }, [data]);

  useAutoRefresh(refresh, { lastFetchedAt });

  return (
    <PageLayout
      title="Dependencies Topology"
      description="Capacity → workspace → item pyramid, with shared storage resources called out explicitly"
      icon={<GitBranch className="h-6 w-6" />}
      helpId="insights.dependencies"
      onRefresh={refresh}
      isRefreshing={isRefreshing}
      lastFetchedAt={lastFetchedAt}
      refreshError={error}
    >
      {error && !data ? (
        <ErrorState title="Failed to load dependencies topology" message={(error as Error).message} onRetry={refresh} />
      ) : isLoading || !data ? (
        <LoadingState rows={8} />
      ) : (
        <>
          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for the full topology tree.
            </Card>
          </QuickGlance>

          <Extended>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div className="space-y-2 xl:col-span-2">
                <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Capacity → Workspace → Item</h2>
                {data.capacities.length === 0 ? (
                  <Card><p className="text-sm text-[var(--color-text-secondary)]">No active capacities</p></Card>
                ) : (
                  data.capacities.map((c) => (
                    <CapacityNode key={c.id} capacity={c} workspaces={data.workspaces} lakehouses={data.lakehouses} warehouses={data.warehouses} onNavigate={navigate} />
                  ))
                )}
              </div>

              <div>
                <h2 className="mb-2 flex items-center gap-1.5 text-base font-semibold text-[var(--color-text-primary)]">
                  <Share2 className="h-4 w-4" /> Shared Storage Resources
                </h2>
                {sharedResources.length === 0 ? (
                  <Card><p className="text-sm text-[var(--color-text-secondary)]">No storage account is shared by more than one item</p></Card>
                ) : (
                  <div className="space-y-2">
                    {sharedResources.map((r) => (
                      <Card key={r.storageAccountId} padding="sm">
                        <div className="mb-1 flex items-center justify-between">
                          <span className="truncate text-sm font-medium text-[var(--color-text-primary)]">{r.name}</span>
                          <Badge variant="warning" isOutlined size="sm">shared by {r.sharedByCount}</Badge>
                        </div>
                        <ul className="space-y-0.5">
                          {r.entities.map((e) => (
                            <li key={`${e.entityType}-${e.entityId}`} className="text-xs text-[var(--color-text-secondary)]">
                              {e.entityName} <span className="opacity-70">({e.entityType})</span>
                            </li>
                          ))}
                        </ul>
                      </Card>
                    ))}
                  </div>
                )}

                {data.capacityDependencies.length > 0 && (
                  <div className="mt-4">
                    <h3 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">Capacity Dependencies</h3>
                    <div className="space-y-1">
                      {data.capacityDependencies.map((d, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="text-[var(--color-text-secondary)]">{d.dependentCapacityName} → {d.dependencyCapacityName}</span>
                          <Badge variant={d.dependencyType === 'Required' ? 'error' : 'secondary'} isOutlined size="sm">{d.dependencyType}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Extended>
        </>
      )}
    </PageLayout>
  );
};

export default DependenciesTopologyPage;
